/**
 * ProgresoTab — admin operaciones panel for managing the 10-step investor
 * milestone bar of a villa.
 *
 * Workflow:
 *   - View / edit the canonical milestones (status, progress %, note).
 *   - Publish a villa-specific update with photos (POST /progress).
 *   - Publish a project-wide construction report (POST /construction-reports).
 *   - Browse and delete construction uploads (UploadsManager).
 *
 * Uses:
 *   GET   /villas/{villa_id}/milestones
 *   PATCH /villas/{villa_id}/milestones/{key}
 *
 * The publish flow + uploads reuse the shared ConstructionProgressPublisher
 * and UploadsManager so admin and constructor stay in sync.
 */
import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import {
  CheckCircle, Circle, ArrowClockwise, Camera, Megaphone, X, NotePencil, FolderOpen, Images,
} from "@phosphor-icons/react";
import { milestoneGlobalPct } from "@/components/investor/ConstructionMilestones";
import ConstructionProgressPublisher, { EstadoDeObraPanel } from "@/components/construction/ConstructionProgressPublisher";
import UploadsManager from "@/components/constructor/UploadsManager";
import ProgressGallery from "@/pages/investor/ProgressGallery";

const STATUS_OPTIONS = [
  { value: "pending",      label: "Pendiente" },
  { value: "in_progress",  label: "En curso" },
  { value: "completed",    label: "Completado" },
];

const STATUS_TONE = {
  pending:     "bg-stone-100 text-stone-600 border-stone-200",
  in_progress: "bg-amber-50 text-amber-800 border-amber-200",
  completed:   "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const DATE_FMT = { day: "numeric", month: "short", year: "numeric" };

export default function ProgresoTab({ villa, villas = [] }) {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingKey, setSavingKey] = useState(null);
  const [editKey, setEditKey] = useState(null);
  const [editDraft, setEditDraft] = useState({ progress_pct: 0, note: "" });
  // publishMode: null | "villa" | "project_all"
  const [publishMode, setPublishMode] = useState(null);
  const [showUploads, setShowUploads] = useState(false);
  const [showPublished, setShowPublished] = useState(false);
  const load = async () => {
    if (!villa?.villa_id) { setMilestones([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/villas/${villa.villa_id}/milestones`);
      setMilestones(data?.milestones || []);
      setError("");
    } catch (e) {
      setError(formatError(e));
      setMilestones([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setEditKey(null);
    setPublishMode(null);
    setShowUploads(false);
    setShowPublished(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [villa?.villa_id]);

  const save = async (m, patch) => {
    setSavingKey(m.key);
    setError("");
    try {
      await api.patch(`/villas/${villa.villa_id}/milestones/${m.key}`, patch);
      await load();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSavingKey(null);
    }
  };

  const openEdit = (m) => {
    setEditKey(m.key);
    setEditDraft({
      progress_pct: typeof m.progress_pct === "number" ? m.progress_pct : 0,
      note: m.note || "",
    });
  };

  const cancelEdit = () => { setEditKey(null); };

  const saveEdit = async (m) => {
    const patch = {
      note: editDraft.note.trim() || null,
    };
    const pct = Number(editDraft.progress_pct);
    if (!Number.isNaN(pct)) patch.progress_pct = Math.max(0, Math.min(100, pct));
    await save(m, patch);
    setEditKey(null);
  };

  if (!villa) {
    return (
      <div className="dv-card text-center py-8 text-stone-500 text-sm">
        {villas.length === 0
          ? "Este inversor todavía no tiene villas asignadas."
          : "Selecciona una villa para gestionar los hitos."}
      </div>
    );
  }

  const completed = milestones.filter((m) => m.status === "completed").length;
  const overallPct = milestoneGlobalPct(milestones);

  return (
    <div data-testid="operaciones-tab-progreso">
      <div
        className="mb-3 text-xs px-3 py-2 rounded border bg-amber-50 border-amber-200 text-amber-900"
        data-testid="progreso-global-notice"
      >
        El inversor ve dos avances independientes: el <strong>general del complejo</strong> (Villas → Estado general de obra)
        y el <strong>de su villa</strong>, que se edita aquí. Los hitos de abajo no cambian ninguno de los dos.
      </div>

      <EstadoDeObraPanel villa={villa} />

      {/* Header card — global progress + workflow actions */}
      <div className="dv-card mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs text-stone-500 uppercase tracking-wider">Avance por hitos de la villa</div>
            <div className="font-serif-dv text-3xl mt-1">{overallPct}%</div>
            <div className="text-xs text-stone-500 mt-1">{completed} / {milestones.length} hitos completados</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => { setPublishMode(publishMode === "villa" ? null : "villa"); }}
              className="dv-btn dv-btn-outline text-sm inline-flex items-center gap-1.5"
              data-testid="progreso-publish-villa-toggle"
            >
              {publishMode === "villa" ? <X size={14} /> : <Camera size={14} />}
              {publishMode === "villa" ? "Cerrar publicador" : "Publicar update"}
            </button>
            <button
              onClick={() => { setPublishMode(publishMode === "project_all" ? null : "project_all"); }}
              className="dv-btn dv-btn-outline text-sm inline-flex items-center gap-1.5"
              data-testid="progreso-publish-project-toggle"
            >
              {publishMode === "project_all" ? <X size={14} /> : <Megaphone size={14} />}
              {publishMode === "project_all" ? "Cerrar publicador" : "Publicar para todas las villas"}
            </button>
            <button
              onClick={() => setShowPublished(v => !v)}
              className="dv-btn dv-btn-ghost text-sm inline-flex items-center gap-1.5"
              data-testid="progreso-published-toggle"
            >
              <Images size={14} />
              {showPublished ? "Ocultar avances" : "Avances publicados"}
            </button>
            <button
              onClick={() => setShowUploads(v => !v)}
              className="dv-btn dv-btn-ghost text-sm inline-flex items-center gap-1.5"
              data-testid="progreso-uploads-toggle"
            >
              <FolderOpen size={14} />
              {showUploads ? "Ocultar archivos" : "Archivos de obra"}
            </button>
            <button onClick={load} className="dv-btn dv-btn-ghost text-sm" data-testid="progreso-refresh">
              <ArrowClockwise size={14} /> Refrescar
            </button>
          </div>
        </div>

        {/* Inline publisher (collapsible) */}
        {publishMode && (
          <div className="mt-4 pt-4 border-t border-stone-200" data-testid="progreso-publisher">
            <ConstructionProgressPublisher
              villaId={villa.villa_id}
              villaName={villa.name}
              isProjectWide={publishMode === "project_all"}
              onPublished={() => {
                // Refresh milestones in case the update touched timeline data
                // that admins want to see reflected immediately.
                load();
              }}
            />
          </div>
        )}
      </div>

      {error && (
        <div className="mb-3 text-sm px-3 py-2 rounded-md border bg-red-50 border-red-200 text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="dv-card text-center py-6 text-stone-500 text-sm">Cargando…</div>
      ) : (
        <ol className="space-y-2" data-testid="operaciones-milestones-list">
          {milestones.map((m) => {
            const isEditing = editKey === m.key;
            return (
              <li
                key={m.key}
                className="dv-card"
                data-testid={`operaciones-milestone-${m.key}`}
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-8 text-center pt-0.5">
                    {m.status === "completed" ? (
                      <CheckCircle size={20} weight="fill" className="text-emerald-600 mx-auto" />
                    ) : (
                      <Circle size={20} className="text-stone-300 mx-auto" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] text-stone-400 tabular-nums">{m.order}.</span>
                      <span className="text-base font-medium">{m.label}</span>
                      <span className={`inline-flex text-[10px] px-2 py-0.5 rounded-full border ${STATUS_TONE[m.status] || STATUS_TONE.pending}`}>
                        {STATUS_OPTIONS.find((o) => o.value === m.status)?.label || m.status}
                      </span>
                      {typeof m.progress_pct === "number" && m.progress_pct > 0 && (
                        <span className="text-[11px] text-stone-500">· {m.progress_pct}%</span>
                      )}
                    </div>
                    {m.completed_at && (
                      <div className="text-[11px] text-stone-500 mt-0.5">
                        Completado el {new Date(m.completed_at).toLocaleDateString("es-ES", DATE_FMT)}
                      </div>
                    )}
                    {!isEditing && m.note && (
                      <div className="text-xs text-stone-600 mt-1 italic whitespace-pre-line">{m.note}</div>
                    )}

                    {/* Inline edit (note + progress_pct) */}
                    {isEditing && (
                      <div className="mt-3 space-y-2" data-testid={`operaciones-milestone-edit-${m.key}`}>
                        <div className="flex items-center gap-2">
                          <label className="text-[10px] uppercase tracking-widest text-stone-500 w-24 shrink-0">Avance %</label>
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={editDraft.progress_pct}
                            onChange={(e) => setEditDraft(d => ({ ...d, progress_pct: e.target.value }))}
                            className="dv-input w-24 text-sm"
                            data-testid={`operaciones-milestone-pct-${m.key}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-widest text-stone-500 block mb-1">Nota</label>
                          <textarea
                            rows={2}
                            value={editDraft.note}
                            onChange={(e) => setEditDraft(d => ({ ...d, note: e.target.value }))}
                            placeholder="Notas internas / contexto del hito"
                            className="dv-input w-full text-sm"
                            data-testid={`operaciones-milestone-note-${m.key}`}
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={cancelEdit}
                            disabled={savingKey === m.key}
                            className="text-xs px-3 py-1 rounded border border-stone-300 text-stone-700 hover:bg-stone-50"
                            data-testid={`operaciones-milestone-cancel-${m.key}`}
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={() => saveEdit(m)}
                            disabled={savingKey === m.key}
                            className="text-xs px-3 py-1 rounded bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50"
                            data-testid={`operaciones-milestone-save-${m.key}`}
                          >
                            {savingKey === m.key ? "Guardando…" : "Guardar"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <select
                      className="dv-input text-xs py-1"
                      value={m.status}
                      disabled={savingKey === m.key}
                      onChange={(e) => save(m, { status: e.target.value })}
                      data-testid={`operaciones-milestone-status-${m.key}`}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    {!isEditing && (
                      <button
                        onClick={() => openEdit(m)}
                        className="text-[11px] text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"
                        data-testid={`operaciones-milestone-edit-btn-${m.key}`}
                      >
                        <NotePencil size={12} /> Editar
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {/* Published updates — reuses the investor gallery, scoped to this villa.
          Admin sees the same view investors see and can remove photos/videos
          uploaded by mistake via the Trash overlay on each thumbnail. */}
      {showPublished && (
        <div className="mt-6" data-testid="progreso-published-section">
          <div className="text-xs text-stone-500 uppercase tracking-wider mb-2">Avances publicados · {villa.name}</div>
          <ProgressGallery initialVillaId={villa.villa_id} hideVillaPicker />
        </div>
      )}

      {/* Uploads — collapsible. Reuses constructor's component; admin has the
          same backend permissions to view/delete construction docs. */}
      {showUploads && (
        <div className="mt-6" data-testid="progreso-uploads-section">
          <div className="text-xs text-stone-500 uppercase tracking-wider mb-2">Archivos de obra · {villa.name}</div>
          <UploadsManager villaId={villa.villa_id} />
        </div>
      )}

      <div className="text-xs text-stone-500 mt-4">
        Los hitos definen la barra de progreso visible para el inversor. El diario fotográfico y el avance por fase se publican desde aquí o desde el panel de Constructor.
      </div>
    </div>
  );
}
